/**
 * userColumns.tsx
 * Sovereign Emerald — Antd v6 column definitions + ToggleStatusCell for
 * the redesigned User Management table.
 *
 * Exports:
 *   getRoleDisplayList  — role / department pairs from a User record
 *   UserColumnHandlers  — handler bag consumed by buildUserColumns
 *   buildUserColumns    — ColumnsType<User> factory
 *   ToggleStatusCell    — confirm-modal toggle component
 */

import React, { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import {
  ShieldCheck,
  Edit,
  Activity,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Calendar,
  Mail,
} from 'lucide-react';

import type { User, RoleInfo } from '@/types/user';
import type { Role } from '@/types/role';
import { ResolvedImage } from '@/components/media/ResolvedImage';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

// ---------------------------------------------------------------------------
// 1. getRoleDisplayList
// ---------------------------------------------------------------------------

/** Returns display pairs of { roleName, department } for a user.
 *  Handles both single-role and multi-role (roles[]) layouts. */
export function getRoleDisplayList(
  user: User,
  allRoles: Role[],
): Array<{ roleName: string; department: string }> {
  const getRoleName = (roleId: string | RoleInfo | undefined): string => {
    if (!roleId) return 'Unknown';
    if (
      typeof roleId === 'object' &&
      'name' in roleId &&
      typeof roleId.name === 'string'
    ) {
      return roleId.name;
    }
    if (typeof roleId === 'string') {
      const r = allRoles.find((x) => (x.id || x._id) === roleId);
      return r?.name ?? 'Unknown';
    }
    return 'Unknown';
  };

  if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles.map((r) => ({
      roleName: getRoleName(r.roleId),
      department: r.department ?? '—',
    }));
  }

  const singleName =
    typeof user.role === 'string'
      ? getRoleName(user.role)
      : user.role &&
          typeof user.role === 'object' &&
          typeof (user.role as RoleInfo).name === 'string'
        ? (user.role as RoleInfo).name
        : 'Unknown';

  const singleDept =
    (user as User & { activeDepartment?: string }).activeDepartment ??
    user.department ??
    '—';

  return [{ roleName: singleName, department: singleDept }];
}

// ---------------------------------------------------------------------------
// 2. UserColumnHandlers
// ---------------------------------------------------------------------------

export interface UserColumnHandlers {
  roles: Role[];
  onEdit: (u: User) => void;
  onActivity: (u: User) => void;
  /** Render the status-toggle cell. Typically: (u) => <ToggleStatusCell user={u} onConfirm={...} /> */
  toggleCell: (u: User) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Avatar element — photo via ResolvedImage, fallback to initials chip. */
function UserAvatar({ user }: { user: User }) {
  const initials = user.name
    ? user.name
        .split(' ')
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase()
    : '?';

  if (user.profilePhoto) {
    return (
      <div className="relative h-8 w-8 shrink-0">
        <ResolvedImage
          mediaSrc={user.profilePhoto}
          alt={user.name}
          className="w-8 h-8 rounded-full object-cover"
          style={{ border: `1px solid ${SOVEREIGN.cardBorder}` }}
        />
      </div>
    );
  }

  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold select-none shrink-0"
      aria-hidden
      style={{
        background: 'rgba(31,90,71,0.08)',
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        color: SOVEREIGN.bottle,
      }}
    >
      {initials}
    </div>
  );
}

/** Small pill for role + department sub-tag. */
function RolePill({
  roleName,
  department,
  muted,
}: {
  roleName: string;
  department: string;
  muted: boolean;
}) {
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded"
      style={
        muted
          ? {
              background: 'rgba(124,138,130,0.08)',
              border: `1px solid rgba(124,138,130,0.20)`,
              color: SOVEREIGN.kpiCaption,
            }
          : {
              background: 'rgba(31,90,71,0.07)',
              border: `1px solid rgba(31,90,71,0.18)`,
              color: SOVEREIGN.bottle,
            }
      }
    >
      <ShieldCheck size={11} aria-hidden />
      <span>{roleName.replace(/_/g, ' ')}</span>
      {department && department !== '—' && (
        <span
          className="text-[10px] px-1 rounded"
          style={
            muted
              ? { background: 'rgba(124,138,130,0.12)', color: SOVEREIGN.kpiCaption }
              : {
                  background: 'rgba(51,146,116,0.12)',
                  color: SOVEREIGN.brand,
                }
          }
        >
          {department}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 3. buildUserColumns
// ---------------------------------------------------------------------------

export function buildUserColumns(handlers: UserColumnHandlers): ColumnsType<User> {
  const { roles, onEdit, onActivity, toggleCell } = handlers;

  return [
    // ── Code ──────────────────────────────────────────────────────────────
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      width: 110,
      render: (code: string) => (
        <span
          className="inline-block text-xs font-mono font-semibold px-2 py-0.5 rounded"
          style={{
            background: SOVEREIGN.inputBg,
            border: `1px solid ${SOVEREIGN.cardBorder}`,
            color: SOVEREIGN.ink,
            letterSpacing: '0.04em',
          }}
        >
          {code || 'Pending'}
        </span>
      ),
    },

    // ── User (avatar + name + email-verified) ─────────────────────────────
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (_: unknown, user: User) => {
        const isInactive = user.status === 'inactive';
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <UserAvatar user={user} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className={`text-sm font-medium truncate ${isInactive ? 'line-through' : ''}`}
                  style={{ color: isInactive ? SOVEREIGN.kpiCaption : SOVEREIGN.ink }}
                >
                  {user.name}
                </span>
                {user.isEmailVerified && (
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0"
                    style={{
                      background: 'rgba(51,146,116,0.10)',
                      border: `1px solid rgba(51,146,116,0.28)`,
                    }}
                    title="Email verified"
                    aria-label="Email verified"
                  >
                    <CheckCircle
                      size={10}
                      aria-hidden
                      style={{ color: SOVEREIGN.brand }}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },

    // ── Email ─────────────────────────────────────────────────────────────
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string, user: User) => {
        const isInactive = user.status === 'inactive';
        return (
          <div className="flex items-center gap-1.5">
            <Mail
              size={13}
              aria-hidden
              style={{ color: isInactive ? SOVEREIGN.kpiCaption : SOVEREIGN.kpiCaption, flexShrink: 0 }}
            />
            <span
              className="text-sm truncate"
              style={{ color: isInactive ? SOVEREIGN.kpiCaption : SOVEREIGN.ink }}
            >
              {email}
            </span>
          </div>
        );
      },
    },

    // ── Roles ─────────────────────────────────────────────────────────────
    {
      title: 'Roles',
      key: 'roles',
      render: (_: unknown, user: User) => {
        const list = getRoleDisplayList(user, roles);
        const muted = user.status === 'inactive';
        return (
          <div className="flex flex-wrap gap-1">
            {list.map(({ roleName, department }, i) => (
              <RolePill
                key={`${roleName}-${i}`}
                roleName={roleName}
                department={department}
                muted={muted}
              />
            ))}
          </div>
        );
      },
    },

    // ── Last Login ────────────────────────────────────────────────────────
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      sorter: true,
      render: (lastLoginAt: string | Date | undefined, user: User) => {
        const isInactive = user.status === 'inactive';
        const color = isInactive ? SOVEREIGN.kpiCaption : SOVEREIGN.kpiCaption;
        if (lastLoginAt) {
          return (
            <div className="flex items-center gap-1.5">
              <Calendar size={13} aria-hidden style={{ color, flexShrink: 0 }} />
              <span className="text-sm" style={{ color }}>
                {formatDistanceToNow(new Date(lastLoginAt), { addSuffix: true })}
              </span>
            </div>
          );
        }
        return (
          <div className="flex items-center gap-1.5">
            <Calendar size={13} aria-hidden style={{ color, flexShrink: 0 }} />
            <span className="text-sm italic" style={{ color: SOVEREIGN.kpiCaption }}>
              Never logged in
            </span>
          </div>
        );
      },
    },

    // ── Actions ───────────────────────────────────────────────────────────
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 160,
      render: (_: unknown, user: User) => {
        if (user.isSuperAdmin) {
          return (
            <div className="flex items-center justify-center">
              <span
                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded"
                style={{
                  background: 'rgba(51,146,116,0.08)',
                  border: `1px solid ${SOVEREIGN.cardBorder}`,
                  color: SOVEREIGN.bottle,
                  letterSpacing: '0.03em',
                }}
              >
                <ShieldCheck size={12} aria-hidden />
                Super Admin
              </span>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-center gap-2">
            {/* Toggle cell (wired by page) */}
            {toggleCell(user)}

            {/* Activity */}
            <button
              type="button"
              title="View activity"
              aria-label={`View activity for ${user.name}`}
              onClick={() => onActivity(user)}
              className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors duration-150"
              style={{
                color: SOVEREIGN.kpiCaption,
                border: `1px solid transparent`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.brand;
                (e.currentTarget as HTMLButtonElement).style.borderColor = SOVEREIGN.cardBorder;
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(51,146,116,0.07)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.kpiCaption;
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <Activity size={15} aria-hidden />
            </button>

            {/* Edit */}
            <button
              type="button"
              title="Edit user"
              aria-label={`Edit ${user.name}`}
              onClick={() => onEdit(user)}
              className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors duration-150"
              style={{
                color: SOVEREIGN.kpiCaption,
                border: `1px solid transparent`,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.bottle;
                (e.currentTarget as HTMLButtonElement).style.borderColor = SOVEREIGN.cardBorder;
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(31,90,71,0.07)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = SOVEREIGN.kpiCaption;
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <Edit size={15} aria-hidden />
            </button>
          </div>
        );
      },
    },
  ];
}

// ---------------------------------------------------------------------------
// 4. ToggleStatusCell
// ---------------------------------------------------------------------------

export interface ToggleStatusCellProps {
  user: User;
  /** Page wires this to the mutation. The cell does NOT call the API itself. */
  onConfirm: () => Promise<void>;
}

export const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({
  user,
  onConfirm,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const isActive = user.status === 'active';

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      toast.success(
        `User ${isActive ? 'deactivated' : 'activated'} successfully`,
      );
      setOpen(false);
    } catch {
      toast.error('Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle track */}
      <button
        type="button"
        title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
        aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${user.name}`}
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center w-10 h-5 rounded-full border select-none transition-all duration-300 focus:outline-none"
        style={
          isActive
            ? {
                background: 'rgba(31,90,71,0.10)',
                borderColor: 'rgba(31,90,71,0.30)',
              }
            : {
                background: 'rgba(124,138,130,0.10)',
                borderColor: 'rgba(124,138,130,0.25)',
              }
        }
      >
        <span
          aria-hidden
          className="w-3 h-3 rounded-full transform transition-all duration-300"
          style={
            isActive
              ? {
                  transform: 'translateX(18px)',
                  background: SOVEREIGN.bottle,
                }
              : {
                  transform: 'translateX(4px)',
                  background: SOVEREIGN.kpiCaption,
                }
          }
        />
      </button>

      {/* Confirm dialog */}
      <AnimatePresence>
        {isOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            style={{ background: 'rgba(42,31,23,0.18)' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="relative w-full max-w-md mx-4 rounded-xl overflow-hidden"
              style={{
                background: SOVEREIGN.cardBg,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
                boxShadow: SOVEREIGN.cardShadow,
              }}
              role="dialog"
              aria-modal
              aria-labelledby="toggle-dialog-title"
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-5 py-4"
                style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
              >
                <div className="flex items-center gap-3">
                  {/* Status icon */}
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    aria-hidden
                    style={
                      isActive
                        ? {
                            background: 'rgba(159,18,57,0.06)',
                            border: '1px solid rgba(159,18,57,0.18)',
                          }
                        : {
                            background: 'rgba(31,90,71,0.07)',
                            border: `1px solid ${SOVEREIGN.cardBorder}`,
                          }
                    }
                  >
                    {isActive ? (
                      <XCircleIcon size={18} style={{ color: '#9F1239' }} aria-hidden />
                    ) : (
                      <CheckCircle size={18} style={{ color: SOVEREIGN.bottle }} aria-hidden />
                    )}
                  </div>
                  <div>
                    <h2
                      id="toggle-dialog-title"
                      className="text-base font-semibold"
                      style={{ color: SOVEREIGN.title }}
                    >
                      {isActive ? 'Deactivate User' : 'Activate User'}
                    </h2>
                    <p
                      className="text-sm"
                      style={{ color: SOVEREIGN.kpiCaption }}
                    >
                      {user.name}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  title="Close"
                  aria-label="Close dialog"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150 disabled:opacity-50"
                  style={{
                    color: SOVEREIGN.kpiCaption,
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                    background: SOVEREIGN.inputBg,
                  }}
                >
                  <X size={14} aria-hidden />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    aria-hidden
                    style={{
                      background: 'rgba(201,169,97,0.08)',
                      border: '1px solid rgba(201,169,97,0.24)',
                    }}
                  >
                    <AlertTriangle
                      size={14}
                      aria-hidden
                      style={{ color: SOVEREIGN.champagne }}
                    />
                  </div>

                  <div className="flex-1">
                    <p
                      className="text-sm leading-relaxed mb-3"
                      style={{ color: SOVEREIGN.ink }}
                    >
                      Are you sure you want to{' '}
                      <span className="font-semibold">
                        {isActive ? 'deactivate' : 'activate'}
                      </span>{' '}
                      <span
                        className="font-semibold"
                        style={{ color: SOVEREIGN.title }}
                      >
                        {user.name}
                      </span>
                      ?
                    </p>

                    <div
                      className="px-3 py-2 rounded-lg text-xs leading-relaxed"
                      style={{
                        background: SOVEREIGN.inputBg,
                        border: `1px solid ${SOVEREIGN.innerRule}`,
                        color: SOVEREIGN.kpiCaption,
                      }}
                    >
                      {isActive
                        ? 'This user will no longer be able to access the system.'
                        : 'This user will regain access to the system.'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex items-center justify-end gap-2.5 px-5 py-4 rounded-b-xl"
                style={{
                  borderTop: `1px solid ${SOVEREIGN.innerRule}`,
                  background: 'rgba(242,234,219,0.35)',
                }}
              >
                {/* Cancel */}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 disabled:opacity-50"
                  style={{
                    color: SOVEREIGN.ink,
                    background: SOVEREIGN.cardBg,
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading)
                      (e.currentTarget as HTMLButtonElement).style.borderColor =
                        SOVEREIGN.inputHoverBorder;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor =
                      SOVEREIGN.cardBorder;
                  }}
                >
                  Cancel
                </button>

                {/* Confirm */}
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-150 disabled:opacity-60"
                  style={
                    isActive
                      ? {
                          background: isLoading ? '#9F1239cc' : '#9F1239',
                          border: '1px solid rgba(159,18,57,0.45)',
                          boxShadow: '0 4px 10px -6px rgba(159,18,57,0.45)',
                        }
                      : {
                          background: isLoading ? `${SOVEREIGN.btn}cc` : SOVEREIGN.btn,
                          border: `1px solid ${SOVEREIGN.btnBorder}`,
                          boxShadow: SOVEREIGN.btnShadow,
                        }
                  }
                >
                  {isLoading ? (
                    <span
                      className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"
                      aria-hidden
                    />
                  ) : null}
                  {isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
